import map from 'lodash/map';
import every from 'lodash/every';
import zipObject from 'lodash/zipObject';
import { createAsyncThunk } from '@reduxjs/toolkit';

// api
import tobaccosApi from '../../../api/tobaccosApi';
import factoryOptionsApi from '../../../api/factoryOptionsApi';
import { TOBACCO_LOCK_OPTIONS } from '../../../pages/Tobaccos/Tobaccos.constants';

// ---------------------------------------------------------------------

const REDUCER_NAME = 'tobaccos';

export const CreateTobacco = createAsyncThunk<any, any>(`${REDUCER_NAME}/createTobacco`, async (data, thunkAPI) => {
  const res = await tobaccosApi.create(data);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetTobaccos = createAsyncThunk<any, { isShowInactive: boolean; readyForUse?: boolean }>(
  `${REDUCER_NAME}/getTobaccos`,
  async (args, thunkAPI) => {
    const { isShowInactive = false, readyForUse = undefined } = args || {};

    const res = await tobaccosApi.get({ isShowInactive, readyForUse });

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetTobaccoById = createAsyncThunk<any, string>(`${REDUCER_NAME}/getTobaccoById`, async (id, thunkAPI) => {
  const res = await tobaccosApi.getById(id);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const UpdateTobacco = createAsyncThunk<any, { data: any; id: string }>(
  `${REDUCER_NAME}/updateTobacco`,
  async ({ data, id }, thunkAPI) => {
    const res = await tobaccosApi.update(data, id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const UpdateTobaccoInactiveStatus = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/updateTobaccoInactiveStatus`,
  async (id, thunkAPI) => {
    const res = await tobaccosApi.updateInactiveStatus(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const ImportTobaccos = createAsyncThunk<any, any>(`${REDUCER_NAME}/importTobaccos`, async (data, thunkAPI) => {
  const res = await tobaccosApi.import(data);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetTobaccoOptions = createAsyncThunk(`${REDUCER_NAME}/getTobaccoOptions`, async (__, thunkAPI) => {
  const optionNames = [
    'tobaccoFarms',
    'tobaccoTypes',
    'tobaccoClassifications',
    'tobaccoSizes',
    'tobaccoSeeds',
    'tobaccoColors',
    'tobaccoYears',
  ];

  const optionResponses = await Promise.all(map(optionNames, (name) => factoryOptionsApi.getByName(name)));

  const allResponsesSuccessful = every(optionResponses, (res) => res.status === 200);

  if (allResponsesSuccessful) {
    const data = map(optionResponses, 'data');
    return zipObject(optionNames, data);
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const CreateFactoryOption = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/createFactoryOption`,
  async (data, thunkAPI) => {
    const res = await factoryOptionsApi.create(data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetTobaccoFactoryConfigForm = createAsyncThunk(
  `${REDUCER_NAME}/getTobaccoFactoryConfigForm`,
  async (__, thunkAPI) => {
    const optionNames = Object.values(TOBACCO_LOCK_OPTIONS);

    const optionResponses = await Promise.all(
      map(optionNames, (name) => factoryOptionsApi.getByFactoryConfigOptionValueName(name))
    );

    const allResponsesSuccessful = every(optionResponses, (res) => res.status === 200);

    if (allResponsesSuccessful) {
      const data = map(optionResponses, (option) =>
        option.data.data ? option.data.data.toString().toUpperCase() : null
      );
      return zipObject(optionNames, data);
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetTobaccoCosts = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/getTobaccoCosts`,
  async (id, thunkAPI) => {
    const res = await tobaccosApi.getTobaccoCosts(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetSimilarTobaccosCosts = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/getSimilarTobaccosCosts`,
  async (id, thunkAPI) => {
    const res = await tobaccosApi.getSimilarTobaccosCosts(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetLastCost = createAsyncThunk<any, string>(`${REDUCER_NAME}/getLastCost`, async (id, thunkAPI) => {
  const res = await tobaccosApi.getLastCost(id);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const SuggestTobaccoCode = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/suggestTobaccoCode`,
  async (data, thunkAPI) => {
    const res = await tobaccosApi.suggestTobaccoCode(data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);
